// Services
import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
//Stores
import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';

// Types
import type { DeviceID } from 'src/types/DeviceID';
import type { MathDevicePropertyOption, MathParameter } from 'src/types/Scenes/MathConditionUI';

// DS
import type { LabelProps } from '@Shelly/shelly-design-system/index';

// Helpers
import { createDeviceID, real_id, shellyId16 } from 'src/helpers/interface_helpers';

import { createHexToDecimalOrId } from 'src/helpers/utilities';

// Helpers: Scenes
import { _device_condition_msg } from 'src/components/Scenes/components/helpers/conditions_utils';
import { _init_conditions_components } from 'src/components/Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/common-helpers';

import {
	extractDeviceChannel,
	extractGuiDeviceId,
	convertSceneCommandToUiProperty,
} from 'src/components/Scenes/helpers/utilities_scenes_to_ui';
import { convertUiPropertyToSceneCommand } from 'src/components/Scenes/helpers/utilities_ui_to_scenes';

import { ID_TO_I18N_PROP } from 'src/components/Scenes/components/helpers/conditions_to_i18n';
import { findImage } from 'src/components/Scenes/components/helpers/main_utils';
import { less_than_more_than_to_label_list } from 'src/components/Scenes/components/helpers/conditions_to_label_list';
import { format_title } from 'src/components/Scenes/SceneWizard/Process/When/Device/helpers';
import { condition_to_character } from 'src/helpers/handle_manage_old_scenes';
import { TEMPERATURE_SENSORS_IN_CELSIUS } from 'src/constants';

const _format = get(format);
const SUPPORTED_PROPERTIES = ['meter', 'temperature', 'humidity', 'vol_meter', 'dist']; // Supported properties for the math condition

/**
 * Get the properties of the selected device, supported by the Math Scenes
 *
 * @param device Device interface object
 * @param status Device status object
 * @param settings Device settings object
 * @returns Array of supported conditions objects for the selected device
 */
export const getDeviceMathProps = (device, status, settings): false | Array<any> => {
	if (!device) return false;

	//Try catch is needed, as some devices may not have the conditions or be WIP.
	try {
		// Get device's Scene conditions
		let device_conditions = _init_conditions_components(device, status, settings) || [];

		// Filter only the supported properties
		return device_conditions.filter((c) => SUPPORTED_PROPERTIES.includes(c?.props?.id));
	} catch (e) {
		console.warn('Error: Unknown device condition props id: ' + device?.id, e);
		return false;
	}
};

/**
 * Converts selected device and condition to IoTs param string ('dev:abc123:meter')
 *
 * @param device_id  GUI Device HEX ID, e.g. 'abc123_1'
 * @param device_property GUI selected contition, e.g. 'meter'
 *
 * @returns IoTs param string, e.g. 'dev:abc123:meter'
 */
export const devicePropertyToIotsParam = (device_id: string, device_property: string): string => {
	let deviceID: DeviceID = extractGuiDeviceId(device_id);
	let dev_decimal_id = createHexToDecimalOrId(deviceID?.id);

	return 'dev:' + dev_decimal_id + ':' + device_property;
};

/**
 * Converts IoTs param string to selected device and condition
 * @param iotsParam IoTs param string, e.g. 'dev:abc123:m1'
 * @returns Object with deviceID and condition
 */
export const iotsParamInterpretator = (
	iotsParam: string, //e.g. 'dev:abc123:m1'
): {
	deviceID: DeviceID; //e.g. {id: 'abc123', channel: 0}
	condition_name: string; //e.g. 'meter'
	iots_condition: string; //e.g. 'm1'
} => {
	if (!iotsParam) return null;
	let iots_params = iotsParam.split(':');
	let devicmalId = iots_params[1];
	let iots_cond = iots_params[2];
	let channel = extractDeviceChannel(iots_cond);
	let deviceId = shellyId16(devicmalId);
	const property = convertSceneCommandToUiProperty(iots_cond)?.name || iots_cond;
	return {
		deviceID: { id: deviceId, channel },
		condition_name: property, //e.g. 'meter'
		iots_condition: iots_cond, //e.g. 'm1'
	};
};

/**
 * Get UI data for the selected Math parameter (device with condition property)
 * @param param Math parameter object
 * @returns Object with device_id, device_name, condition, avatar and icon
 */
export const getMathParamData = (
	param: MathParameter,
): {
	device_id: string;
	device_name: string;
	avatar: { image?: string; backgroundColor?: string; icon?: string };
	condition: string; //Translated condition
	icon: string; //Icon for the condition
	units?: string; //Unit for the condition
} => {
	const _devices = get(devices);
	let paramData = iotsParamInterpretator(param.value);
	let type = _devices?.[paramData.deviceID.id]?.type;

	const device_id = createDeviceID(paramData.deviceID.id, paramData.deviceID.channel, type);
	let condition_name = paramData.condition_name; //e.g. 'meter', 'temperature'

	const _device = _devices[device_id];
	const _settings = get(devices_settings);
	const _status = get(devices_status);
	const isOnline = _status?.[real_id(device_id)]?.online || !_settings[real_id(device_id)];

	let device_name = _device?.name || _format('unknown_device');
	if (!isOnline) {
		device_name += ' - ' + _format('label_offline');
	}

	let avatar: { image?: string; backgroundColor?: string; icon?: string } = findImage(_device);
	if (avatar.image === undefined && avatar.backgroundColor === undefined) {
		avatar = { icon: _device?.icon || 'fa fa-trash' };
	}

	let condition_i18 = ID_TO_I18N_PROP(condition_name, _device?.gen);

	let icon = getConditionIcon(condition_name);

	// Get the condition unit, depending on the device type
	// Note: Legacy inheritance - looks like for some devices is stored in the device interface data
	//  and might not provide it in the settings.
	const units = condition_to_character(
		condition_name,
		!TEMPERATURE_SENSORS_IN_CELSIUS.includes(_device?.type)
			? devices_settings.getByRealId(device_id)
			: _device,
	);

	return {
		device_id,
		device_name: device_name,
		condition: condition_i18,
		icon,
		avatar,
		units,
	};
};

/**
 * Get the icon for the selected condition
 *  @ToDo: Move to the conditions utils or refactor underlying logic
 * @param condition_name Condition name
 * @returns Icon for the condition
 */
let getConditionIcon = (condition_name: string): string => {
	let conditionLabelList = less_than_more_than_to_label_list(condition_name, '-', '', '');
	return conditionLabelList?.icon;
};

/**
 * Math paramets wizard: Prepare preview data for the selected Math parameter
 * @param param Math parameter object
 * @returns
 */
export const getMathParamPreview = (param: MathParameter) => {
	let { device_name, avatar, icon, condition, units } = getMathParamData(param);

	const condition_text = condition + (units ? ` (${units})` : '');
	let labelList: LabelProps[] = [
		{
			icon: icon, //Parameter icon
			value: condition_text, //Translated condition + unit
		},
	];

	return {
		title: device_name,
		avatar: {
			image: avatar?.image,
			backgroundColor: avatar?.backgroundColor,
			icon: avatar?.icon,
		},
		withLabelList: true,
		labelList,
	};
};

/**
 * Scene preview: device & property used as math parameters
 * @param param
 * @returns Object with device name, icon and translated condition
 */

export const getMathParamConditionListItem = (
	param: MathParameter,
): {
	name: string; //GUI Device name and Translated condition property
	icon: string; //Icon for the condition
} => {
	let { icon, device_name, condition, units } = getMathParamData(param);

	let conditionItem = {
		//Device name and Translated condition property
		name:
			param.name + // A,B,C, etc
			': ' +
			device_name + //Device name
			': ' +
			condition +
			(units ? ` (${units})` : ''), //Unit if available
		icon,
	};

	return conditionItem;
};

/**
 * Device properties for the Math Scenes
 *
 * Prepares the Property Options from the Device condition components, supported by Math Scenes
 *
 * @param {string} device_id Device ID (e.g. 'abc123_1')
 * @returns Array of objects with title, value and disabled properties
 */
export const getDevicePropertyOptions = (device_id: string): Array<MathDevicePropertyOption> => {
	const _device = get(devices)[device_id];
	let components = _init_conditions_components(
		_device,
		devices_status.getByRealId(device_id),
		devices_settings.getByRealId(device_id),
	);
	if (!_device || !components.length) {
		return [];
	}

	const device_channel = _device?.channel || 0;

	return components
		.filter((el) => SUPPORTED_PROPERTIES.includes(el?.props?.id))
		.map((el) => {
			const property_channel = el.props?.add_info_ind || device_channel;
			const iots_property = convertUiPropertyToSceneCommand(el.props.id, property_channel);
			return {
				title: format_title(el), //Translated i18n property name
				add_info_ind: el.props?.add_info_ind, // Channel for addons
				value: iots_property, // e.g. 'm1', 'hum101'
				disabled: el?.props?.disabled || false,
			};
		});
};
